/**
 * Drag-to-scroll for the landing page slider (`sections/dev-lp-slider.liquid`).
 *
 * The track is a native scroll-snap scroller, so touch swipe, trackpad and shift+wheel
 * already work with no JS. This adds the one gesture native scrolling does not give a
 * desktop mouse user: click and drag. It therefore attaches on `pointerType === 'mouse'`
 * only — hijacking touch would cost momentum scrolling and gain nothing.
 *
 * Snap is suspended while dragging (it fights programmatic `scrollLeft`) and restored on
 * release, so letting go settles on the nearest slide.
 */

const DRAG_THRESHOLD = 5;

const wire = (track) => {
  if (track.dataset.lpDragWired === 'true') return;
  track.dataset.lpDragWired = 'true';
  track.classList.add('dev-lp-slider__track--draggable');

  let pointerId = null;
  let startX = 0;
  let startScroll = 0;
  let dragged = false;

  track.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (track.scrollWidth <= track.clientWidth) return;
    pointerId = event.pointerId;
    startX = event.clientX;
    startScroll = track.scrollLeft;
    dragged = false;
  });

  track.addEventListener('pointermove', (event) => {
    if (event.pointerId !== pointerId) return;
    const dx = event.clientX - startX;

    if (!dragged) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      dragged = true;
      track.classList.add('dev-lp-slider__track--dragging');
      track.setPointerCapture(pointerId);
    }

    track.scrollLeft = startScroll - dx;
    event.preventDefault();
  });

  const endDrag = (event) => {
    if (event.pointerId !== pointerId) return;
    if (track.hasPointerCapture?.(pointerId)) track.releasePointerCapture(pointerId);
    track.classList.remove('dev-lp-slider__track--dragging');
    pointerId = null;
  };

  track.addEventListener('pointerup', endDrag);
  track.addEventListener('pointercancel', endDrag);

  // A drag that actually moved must not activate the link under the cursor.
  track.addEventListener(
    'click',
    (event) => {
      if (!dragged) return;
      event.preventDefault();
      event.stopPropagation();
      dragged = false;
    },
    true
  );

  // Native image dragging would fight the gesture.
  track.addEventListener('dragstart', (event) => {
    if (pointerId !== null) event.preventDefault();
  });
};

const init = (root) => {
  for (const track of (root ?? document).querySelectorAll('[data-lp-slider-track]')) wire(track);
};

init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
